import { TUserAction } from '../__shared/shared.interfaces';

export enum TDocumentStatus {
  SUBMITTED = 'submitted',
  APPROVED = 'approved',
  PENDING = 'pending',
  VERIFIED = 'verified',
  REJECTED = 'rejected',
  EXPIRED = 'expired',
  CANCELLED = 'cancelled',
  INVALID = 'invalid',
  OTHER = 'other',
}

export enum TDocumentType {
  BIRTH_CERTIFICATE = 'birthCertificate',
  TRANSFER_CERTIFICATE = 'transferCertificate',
  PREVIOUS_MARKSHEET = 'previousMarksheet',
  MEDICAL_CERTIFICATE = 'medicalCertificate',
  AADHAR_CARD = 'aadharCard',
  PAN_CARD = 'panCard',
  SALARY_SLIP = 'salarySlip',
  RESIGNATION_LETTER = 'resignationLetter',
  JOINING_LETTER = 'joiningLetter',
  PROMOTION_LETTER = 'promotionLetter',
  LEAVE_LETTER = 'leaveLetter',
  HR_DOCUMENT = 'hrDocument',
  PHOTO = 'photo',
  OTHER = 'other',
}

export enum TDocumentOwnerType {
  USER = 'user',
  STUDENT = 'student',
  FINANCE = 'finance',
  INVENTORY = 'inventory',
  INSTITUTE = 'institute',
}

export interface TDocument {
  documentId: string;
  userId: string;
  documentType: TDocumentType;
  documentName: string;
  submissionDate: string;
  verificationDate?: string;
  status: TDocumentStatus;
  remarks?: string;
  fileUrl?: string;
  thumbnailUrl?: string;
  description?: string;
  uploadedAt: TUserAction;
}

export interface TUserDocumentSummaryMap {
  id: string;
  basicDetails: {
    firstName: string;
    middleName?: string;
    lastName: string;
  };
  documents: TDocument[];
}

export interface TCreateDocumentRequest {
  name: string;
  description?: string;
  ownerType: TDocumentOwnerType;
  ownerId: string;
  instituteId?: string;
  createdBy: string;
  verificationDate?: string;
  isVerifiable?: boolean;
}

export interface TListDocumentsQuery {
  page?: number;
  limit?: number;
  userId?: string;
  instituteId?: string;
  documentType?: DocumentType;
  status?: TDocumentStatus;
  isVerifiable?: boolean;
  ownerType?: TDocumentOwnerType;
}

export interface TVerifyDocumentRequest {
  notes?: string;
}

export interface TRejectDocumentRequest {
  reason: string;
}

export interface TQueryDocumentsByOwnerRequest {
  ownerType: TDocumentOwnerType;
  ownerId: string;
  documentType?: DocumentType;
  status?: TDocumentStatus;
}

export interface TDocumentResponse {
  id: string;
  name: string;
  description?: string;
  documentType: DocumentType;
  status: TDocumentStatus;
  isVerifiable: boolean;
  fileKey: string;
  signedUrl: string;
  thumbnailKey?: string;
  thumbnailSignedUrl?: string;
  ownerType: TDocumentOwnerType;
  ownerId: string;
  instituteId?: string;
  verifiedBy?: string;
  verifiedAt?: string;
  verificationNotes?: string;
  rejectionReason?: string;
  createdAt: string;
}

export interface TDocumentListResponse {
  data: TDocumentResponse[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface TVerificationAttributes {
  is_verifiable: boolean;
  verified_by?: string;
  verified_at?: Date;
  verification_notes?: string;
  rejection_reason?: string;
}

export interface TDocumentAttributes extends TVerificationAttributes {
  _id: string;
  name: string;
  description: string;
  document_type: DocumentType;
  status: TDocumentStatus;
  file_key: string;
  thumbnail_key?: string;
  owner_type: TDocumentOwnerType;
  owner_id: string;
  institute_id?: string;
  created_at?: Date;
  updated_at?: Date;
  created_by?: string;
  updated_by?: string;
}

export interface TDocumentCreationAttributes {
  name: string;
  description?: string;
  document_type: DocumentType;
  status: TDocumentStatus;
  file_key: string;
  thumbnail_key?: string;
  owner_type: TDocumentOwnerType;
  owner_id: string;
  institute_id?: string;
  is_verifiable: boolean;
  created_by: string;
}

export const VERIFIABLE_DOCUMENT_TYPES: TDocumentType[] = [
  TDocumentType.BIRTH_CERTIFICATE,
  TDocumentType.TRANSFER_CERTIFICATE,
  TDocumentType.PREVIOUS_MARKSHEET,
  TDocumentType.MEDICAL_CERTIFICATE,
  TDocumentType.AADHAR_CARD,
];

export const DEFAULT_SIGNED_URL_EXPIRY = 3600;

export const DOCUMENT_OWNER_TYPES = {
  USER: 'user',
  STUDENT: 'student',
  FINANCE: 'finance',
  INVENTORY: 'inventory',
  INSTITUTE: 'institute',
} as const;

export interface TFileUploadMetadata {
  originalName: string;
  mimeType: string;
  size: number;
  fileKey: string;
  thumbnailKey?: string;
}

export interface TDocumentStats {
  total: number;
  byStatus: {
    [key in TDocumentStatus]?: number;
  };
  byType: {
    [key in TDocumentType]?: number;
  };
  verifiable: number;
  verified: number;
  rejected: number;
  pendingVerification: number;
}

export interface TDocumentAPI {
  createDocument: {
    request: TCreateDocumentRequest & { file: File };
    response: TDocumentResponse;
  };
  listDocuments: {
    query: TListDocumentsQuery;
    response: TDocumentResponse[];
  };
  getDocument: {
    params: { id: string };
    response: TDocumentResponse;
  };
  verifyDocument: {
    params: { id: string };
    body: TVerifyDocumentRequest;
    response: TDocumentResponse;
  };
  rejectDocument: {
    params: { id: string };
    body: TRejectDocumentRequest;
    response: TDocumentResponse;
  };
}

export function isValidOwnerType(type: string): type is TDocumentOwnerType {
  return Object.values(TDocumentOwnerType).includes(type as TDocumentOwnerType);
}

export function isValidDocumentStatus(
  status: string
): status is TDocumentStatus {
  return Object.values(TDocumentStatus).includes(status as TDocumentStatus);
}

export function isValidDocumentType(type: string): type is TDocumentType {
  return Object.values(TDocumentType).includes(type as TDocumentType);
}

export function isVerifiableDocumentType(type: TDocumentType): boolean {
  return VERIFIABLE_DOCUMENT_TYPES.includes(type);
}

export function canBeVerified(status: TDocumentStatus): boolean {
  return (
    status === TDocumentStatus.PENDING || status === TDocumentStatus.SUBMITTED
  );
}

export function isFinalState(status: TDocumentStatus): boolean {
  return (
    status === TDocumentStatus.VERIFIED || status === TDocumentStatus.REJECTED
  );
}

// ---------------- Document Details Endpoint Interfaces -----------------
export interface TDocumentDetails {
  basic_details: TDocumentBasicDetails;
  owner: TDocumentOwner;
  verifier?: TDocumentVerifier;
  institute?: TDocumentInstituteDetails;
  action: TDocumentUserAction;
}

export interface TDocumentBasicDetails {
  _id: string;
  name: string;
  type: string; // Document type enum value
  size: number;
  s3_key: string;
  thumbnail_key?: string;
  is_verifiable: boolean;
  verified: boolean;
  verified_at?: Date;
  verification_notes?: string;
  rejection_reason?: string;
}

export interface TDocumentOwner {
  owner_type: string; // 'user' | 'student' | 'finance' | 'inventory'
  owner_id: string;
  owner_name?: string;
  owner_details?: any; // Type-specific details based on owner_type
}

export interface TDocumentVerifier {
  _id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

export interface TDocumentInstituteDetails {
  _id?: string;
  name?: string;
}

export interface TDocumentUserAction {
  created_at: Date;
  created_by: string;
  updated_at: Date;
  updated_by: string;
}
